'use client';

import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';

export interface BreadcrumbItem {
  name: string;
  href?: string;
}

interface HeaderContextType {
  customTitle: string | null;
  setCustomTitle: (title: string | null) => void;
  customBreadcrumbs: BreadcrumbItem[] | null;
  setCustomBreadcrumbs: (breadcrumbs: BreadcrumbItem[] | null) => void;
  customBackAction: (() => void) | null;
  setCustomBackAction: (action: (() => void) | null) => void;
  customHeaderActions: React.ReactNode | null;
  setCustomHeaderActions: (actions: React.ReactNode | null) => void;
  customLeftActions: React.ReactNode | null;
  setCustomLeftActions: (actions: React.ReactNode | null) => void;
  hideTitle: boolean;
  setHideTitle: (hide: boolean) => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export function HeaderProvider({ children }: { children: ReactNode }) {
  const [customTitle, setCustomTitle] = useState<string | null>(null);
  const [customBreadcrumbs, setCustomBreadcrumbs] = useState<BreadcrumbItem[] | null>(null);
  const [customBackAction, setCustomBackAction] = useState<(() => void) | null>(null);
  const [customHeaderActions, setCustomHeaderActions] = useState<React.ReactNode | null>(null);
  const [customLeftActions, setCustomLeftActions] = useState<React.ReactNode | null>(null);
  const [hideTitle, setHideTitle] = useState<boolean>(false);

  // The setters are stable useState setters; memoizing keeps consumers from re-rendering
  // when the provider re-renders without any header field changing.
  const value = useMemo(
    () => ({
      customTitle,
      setCustomTitle,
      customBreadcrumbs,
      setCustomBreadcrumbs,
      customBackAction,
      setCustomBackAction,
      customHeaderActions,
      setCustomHeaderActions,
      customLeftActions,
      setCustomLeftActions,
      hideTitle,
      setHideTitle,
    }),
    [customTitle, customBreadcrumbs, customBackAction, customHeaderActions, customLeftActions, hideTitle]
  );

  return (
    <HeaderContext.Provider value={value}>
      {children}
    </HeaderContext.Provider>
  );
}

export function useHeaderContext() {
  const context = useContext(HeaderContext);
  // Return default values if context is not available (optional usage)
  return context || {
    customTitle: null,
    setCustomTitle: () => {},
    customBreadcrumbs: null,
    setCustomBreadcrumbs: () => {},
    customBackAction: null,
    setCustomBackAction: () => {},
    customHeaderActions: null,
    setCustomHeaderActions: () => {},
    customLeftActions: null,
    setCustomLeftActions: () => {},
    hideTitle: false,
    setHideTitle: () => {},
  };
}

